// Per-service extras: rich content for the FATJOE-style service detail template.
// Keyed by slug. Each entry has: hero_bullets, process[3], examples[], benefits[] (with icon+title+desc),
// reviews { rating, of, count, recommend_pct, sample[] }, faqs[], guarantee.
import {
  Zap,
  Award,
  TrendingUp,
  ShieldCheck,
  PenTool,
  Globe2,
  Search as SearchIcon,
  Target,
  Layers,
  Compass,
  MapPin as MapPinIcon,
  Users,
  ListChecks as ListChecksIcon,
  Wand2,
  Sparkles as SparkleIcon,
  CheckCircle2,
} from "lucide-react";

const EXAMPLE_IMAGES = [
  "https://images.unsplash.com/photo-1707336862166-1e483cfa5c92?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1755502046551-102bcdc6f0c6?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1743046813915-94cf6d5e6942?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1758691736979-ff263c04b3d1?crop=entropy&cs=srgb&fm=jpg&q=85",
];

const DEFAULT_REVIEWS = {
  rating: "4.8/5",
  count: "1,930+",
  recommend_pct: 95,
  sample: [
    { name: "James Mclchry", quote: "So far so good. Reporting is crisp and delivery on time.", days: "4 days ago" },
    { name: "Philip Durritz", quote: "Easy to use order process, hopefully it works long-term.", days: "11 days ago" },
    { name: "Michael Lyle", quote: "Smooth and simple process. Will reorder.", days: "16 days ago" },
  ],
};

export const SERVICE_EXTRAS = {
  "press-release-distribution": {
    tagline: "Press Release Distribution On-Demand",
    hero_bullets: [
      "Premium-quality press release distribution channels",
      "Guaranteed placements via proactive PR",
      "High-quality releases written with unlimited revisions",
    ],
    process: [
      { title: "We write the perfect press release", body: "Our editorial team produces a newsroom-quality release with up to two free revisions until you're delighted." },
      { title: "We distribute the press release", body: "We push it straight into the press release distribution network — hitting the best possible outlets for your niche." },
      { title: "View your placements", body: "Get a single publication of the live placements with full white-label reporting you can hand to clients." },
    ],
    examples: EXAMPLE_IMAGES,
    benefits: [
      { icon: Zap, title: "Instant proactive PR", body: "Get instant online exposure from top press release sites and verified media networks." },
      { icon: PenTool, title: "Professionally written", body: "Our editors produce professional press releases with unlimited revisions before distribution." },
      { icon: TrendingUp, title: "Increase brand authority", body: "Gives the impression you are the leading authority in your industry — across major publishers." },
      { icon: Globe2, title: "Natural promotion", body: "Companies and brands were distributing press releases way before search engines existed." },
    ],
    side_logos: ["LUIS P · TrustPilot", "Goldie Agency", "Business Beginners"],
    guarantee_text: "We believe in our Press Release Distribution service so much that we'll make your order completely risk-free. If it doesn't meet our standards, we'll refund you in full. Simple as that.",
    faqs: [
      { q: "What is a Press Release Distribution Service and how does it help SEO?", a: "We distribute a professionally-written press release to a network of news syndication sites and Google News partners. The result: brand mentions, citation signals, and referral traffic that strengthens topical authority." },
      { q: "What is a press release?", a: "A press release is a short, newsworthy announcement (under ~600 words) that journalists and editors can use as the basis for coverage." },
      { q: "Why should I hire a professional press release writer?", a: "Editors junk releases that read like ad copy. Our writers know how to structure a release that earns pickups, not just impressions." },
      { q: "How many sites can I expect to see my press release on?", a: "Typical campaigns generate 100–250+ live pickups across syndication networks and Google News partners." },
      { q: "How many link mentions can I have in the press release?", a: "Up to 2 contextual links inside the release, plus 1 in the boilerplate. Anything more risks editor rejection." },
      { q: "Are all the links followed?", a: "Most network mirrors use nofollow. Direct pickups by publishers may grant dofollow links. The value is brand authority + referral traffic + citation signals." },
      { q: "Do I get a full report?", a: "Yes — a white-label PDF + CSV showing every live URL, the publisher, and the cached snapshot." },
      { q: "Who should I add as the press contact?", a: "Either your PR lead or our generic contact — we route inbound press inquiries to you within 24 hours." },
      { q: "What contact information do you include?", a: "Company name, email, optional phone, and a single landing URL." },
      { q: "Which countries does your distribution service cover?", a: "Global English-language news networks, with optional UK/US/AU/CA regional emphasis on Pro and Ultimate." },
    ],
    reviews: DEFAULT_REVIEWS,
  },

  "content-syndication": {
    tagline: "Save 100+ hours of outreach. Republish what already ranks. You take the credit.",
    hero_bullets: [
      "Manual, relevant outreach to authority blogs",
      "From aged content already ranked and indexed in Google",
      "Lifetime link guarantee & 100% money-back guarantee",
    ],
    process: [
      { title: "The outreach stage", body: "You'll provide the anchor text and target URL. Our outreach team will scour the web for blog posts that are perfect to add value to." },
      { title: "The content stage", body: "Our content team crafts an additional contextual addition to the existing post that adds value to the blog post and incorporates your link naturally." },
      { title: "View your placements", body: "You'll get as easy as every single content syndication placement available — exportable as a white-label CSV." },
    ],
    examples: EXAMPLE_IMAGES,
    benefits: [
      { icon: Award, title: "Genuine outreach", body: "We secure niche edits on sites we've genuinely reached out to — no PBNs, no link farms." },
      { icon: Layers, title: "In-content links", body: "Links are incorporated editorially within the body of original content, not in author bios or sidebars." },
      { icon: ShieldCheck, title: "Lifetime link guarantee", body: "We offer a lifetime guarantee on every placement — if anything goes thin, we replace it." },
      { icon: CheckCircle2, title: "No duplicate links", body: "We cross-check Ahrefs API to avoid duplicate referring domains across your portfolio." },
    ],
    side_logos: ["Gotch SEO", "Web Hosting Cat", "Money Empire"],
    guarantee_text: "We believe in our content syndication service so much that we'll make your order completely risk-free. If we don't meet our guarantees, we'll refund you in full.",
    faqs: [
      { q: "What is content syndication and how does it help SEO?", a: "We secure contextual additions inside existing, indexed articles on authority blogs. The signal: editorial co-citation that already has ranking equity." },
      { q: "How is this different from broken link building?", a: "Broken link building waits for 404s. We don't — we proactively craft content additions that improve the post itself, in exchange for a contextual link." },
      { q: "How is this better than a regular link building service?", a: "Higher relevance + the host page already has trust signals (indexed, ranking, referring domains). Faster to lift rankings on competitive terms." },
      { q: "Can I choose the Domain Rating of the blogs?", a: "Yes — DR 10+ through DR 60+ tiers. Pick the band that fits your budget." },
      { q: "Who writes the additional content for the blogs?", a: "Our in-house editorial team. You can request a tone or supply guidelines; we don't outsource to mills." },
      { q: "How relevant will the content be?", a: "Topical and contextual. Anchor text is selected by you and slotted naturally into the new section." },
      { q: "Can I place one large order with multiple client domains?", a: "Yes — bulk and multi-domain orders are supported at the agency tier." },
      { q: "Can we approve content or sites before you place the links?", a: "Yes — pre-approval is available on every order above DR 30+." },
      { q: "What countries do you secure placements in?", a: "Predominantly US, UK, Canada, Australia. Geo-specific bands available." },
      { q: "Where are the links placed?", a: "Inside the body content, contextually, within added paragraphs — never in sidebars or footers." },
      { q: "How long will these placements last?", a: "Lifetime — we monitor and replace anything that drops." },
      { q: "What if you can't find any relevant websites?", a: "Full refund. We won't force-fit a placement to hit a deadline." },
      { q: "Do you take orders for adult, gambling or pharmaceuticals?", a: "Not at this time." },
      { q: "Can I include regional identifiers in my anchor texts?", a: "Yes — city, country and regional modifiers are fully supported." },
    ],
    reviews: DEFAULT_REVIEWS,
  },

  "guest-posting": {
    tagline: "Authority guest post placements categorized by DR.",
    hero_bullets: [
      "Real websites · niche relevance · manual outreach",
      "Original content written by our editorial team",
      "SEO-safe placements — no PBNs, never",
    ],
    process: [
      { title: "Tell us the niche & anchor", body: "Provide your URL, anchor preferences, and target niche. Pick a DR tier that fits your campaign." },
      { title: "We outreach & write", body: "Manual outreach to vetted publishers in your niche. Editorial team writes the article from scratch." },
      { title: "Live placement & report", body: "You get a live URL, screenshots, traffic data, and a white-label PDF for client delivery." },
    ],
    examples: EXAMPLE_IMAGES,
    benefits: [
      { icon: Award, title: "Real, indexed websites", body: "Every host site has live organic traffic and a real editorial calendar — we vet before we publish." },
      { icon: Target, title: "Niche-relevant only", body: "Topical fit is non-negotiable. Off-topic placements hurt rankings more than they help." },
      { icon: PenTool, title: "Original editorial content", body: "Our writers produce custom articles per placement — we never reuse content." },
      { icon: ShieldCheck, title: "SEO-safe & white-label", body: "No PBNs. No link wheels. Reports carry your logo, not ours." },
    ],
    side_logos: ["Authority Tracker", "Niche Lab", "Outreach Pros"],
    guarantee_text: "If a placement is removed or noindexed in the first 90 days, we replace it at no cost. Period.",
    faqs: [
      { q: "Are the links permanent?", a: "Yes. Placements go on real websites with editorial intent and are intended to be permanent. We replace rare removals." },
      { q: "Do you write the content?", a: "Yes — all content is produced by our editorial team and approved before placement." },
      { q: "Is this white-label?", a: "Absolutely. Every deliverable is unbranded for agency resale." },
      { q: "What niches do you cover?", a: "80+ niches including SaaS, finance, health, e-commerce, B2B and local services." },
      { q: "How long does delivery take?", a: "10–28 days depending on DR tier. Live ETAs appear in your dashboard." },
    ],
    reviews: DEFAULT_REVIEWS,
  },

  "high-end-backlinks": {
    tagline: "Premium editorial backlinks from real publications.",
    hero_bullets: [
      "DR 60+ flagship publishers with verified traffic",
      "Editor-led pitch process, no PBNs",
      "Screenshots & live URLs included with every order",
    ],
    process: [
      { title: "Pitch development", body: "We brief your offer + topic angle to senior editors at flagship publishers." },
      { title: "Editorial placement", body: "Our writers handle revisions with the editor until the piece publishes." },
      { title: "Live link & reporting", body: "You receive the live URL, screenshots and a publisher-traffic snapshot." },
    ],
    examples: EXAMPLE_IMAGES,
    benefits: [
      { icon: Award, title: "Genuine editorial trust", body: "Placements that earn editorial review carry weight algorithms can detect." },
      { icon: TrendingUp, title: "Real ranking lift", body: "Designed for moving the needle on competitive head-terms." },
      { icon: ShieldCheck, title: "Brand-safe", body: "Only sites that fit your brand reputation — we filter aggressively." },
      { icon: Wand2, title: "Hands-off process", body: "We handle pitch through publication. You approve the angle, we ship." },
    ],
    side_logos: ["Editorial DR70", "Press Network", "Tier-1 Outlets"],
    guarantee_text: "Money-back guarantee: if we can't place within 30 days, we refund — no questions.",
    faqs: [
      { q: "What DR are these from?", a: "DR 60+ on Core and DR 70+ on Premium. All publishers have verified organic traffic." },
      { q: "Are these permanent?", a: "Yes — editorial placements on real outlets." },
      { q: "What about traffic?", a: "Every publisher we use has documented organic traffic on the topic." },
    ],
    reviews: DEFAULT_REVIEWS,
  },

  "local-citations": {
    tagline: "Local citation building & NAP consistency at scale.",
    hero_bullets: [
      "USA · UK · Australia · Canada local directories",
      "NAP consistency across 25–80+ listings",
      "Google Business Profile optimization included",
    ],
    process: [
      { title: "Submit business details", body: "Provide your NAP, business categories, and Google Business Profile access." },
      { title: "Manual submissions", body: "Our team submits manually to each directory — no spam-tier bots." },
      { title: "Login & report delivery", body: "You receive a CSV of every submission, including logins for future edits." },
    ],
    examples: EXAMPLE_IMAGES,
    benefits: [
      { icon: MapPinIcon, title: "NAP consistency", body: "Aligned name, address, phone across the local citation graph for ranking trust." },
      { icon: TrendingUp, title: "Local rank improvements", body: "Citation density still moves the map pack — especially for new or thin profiles." },
      { icon: ShieldCheck, title: "Google Business support", body: "Includes GBP category audit, photo prompts, and Q&A seeding." },
      { icon: Globe2, title: "Country-specific", body: "Submissions only to directories that serve your country and niche." },
    ],
    side_logos: ["Map Pack Pro", "GBP Lab", "Local Signal"],
    guarantee_text: "If a directory rejects your submission, we replace it with an equal-quality alternative at no extra charge.",
    faqs: [
      { q: "Which countries do you serve?", a: "USA, UK, Australia, Canada — country-specific directories only." },
      { q: "Will my listings be live?", a: "Yes — submissions are tracked and only counted when live." },
      { q: "Do you include GBP work?", a: "Yes, GBP audit + optimization is included from Pro tier." },
    ],
    reviews: DEFAULT_REVIEWS,
  },

  "on-page-seo": {
    tagline: "On-page SEO that compounds — metadata, structure, schema.",
    hero_bullets: [
      "Title & meta rewrites optimized per query",
      "Header structure, internal linking, schema",
      "Crawlability and Core Web Vitals hints",
    ],
    process: [
      { title: "Audit your pages", body: "We crawl your pages and benchmark against intent and competitors." },
      { title: "Rewrites & schema", body: "Title, meta, headers, schema and internal links are rewritten per page." },
      { title: "Delivery & QA", body: "You get a doc with all changes, ready to implement (or we implement for WP/Webflow)." },
    ],
    examples: EXAMPLE_IMAGES,
    benefits: [
      { icon: SearchIcon, title: "Higher CTR", body: "Better titles + metas earn more clicks at the same rank." },
      { icon: Target, title: "Sharper keyword targeting", body: "Each page aligns to a clear intent and primary keyword cluster." },
      { icon: Compass, title: "Improved crawlability", body: "Internal links unblock crawl traps and orphan pages." },
      { icon: ListChecksIcon, title: "Schema where it matters", body: "Article, FAQ, Product, Breadcrumbs — only where supported." },
    ],
    side_logos: ["On-Page Lab", "Schema Pro", "CTR Boost"],
    guarantee_text: "If we don't surface 25+ prioritized issues across your pages, your order is on us.",
    faqs: [
      { q: "Will you implement the changes?", a: "Yes — included on WordPress, Webflow and Shopify on Pro tier. Otherwise we deliver a structured doc." },
      { q: "Does this include content rewrites?", a: "Title and meta yes; full body content rewriting is optional add-on." },
    ],
    reviews: DEFAULT_REVIEWS,
  },

  "site-audit": {
    tagline: "Detailed SEO audit covering technical, content & speed.",
    hero_bullets: [
      "Technical SEO scan + index coverage",
      "Content gap & competitor analysis",
      "Action plan with prioritized fixes",
    ],
    process: [
      { title: "Crawl & data pull", body: "Full-site crawl plus rank, traffic, and backlink data." },
      { title: "Analysis & prioritization", body: "We rank every issue by impact × effort to give you a clean roadmap." },
      { title: "Walkthrough delivery", body: "Loom walkthrough + Notion/Doc + CSV of issues. Pro tier includes a 60-min call." },
    ],
    examples: EXAMPLE_IMAGES,
    benefits: [
      { icon: Zap, title: "Find blockers fast", body: "Surface the 5 issues that move the needle, not 500 noise items." },
      { icon: ListChecksIcon, title: "Prioritized fixes", body: "Every recommendation tagged with effort and predicted impact." },
      { icon: Compass, title: "A real growth roadmap", body: "Outcome-led, not checklist-led." },
      { icon: Users, title: "Built for handoff", body: "Designed to brief engineers, content teams, and clients without translation." },
    ],
    side_logos: ["Crawl Co", "Index Lab", "Speed Insights"],
    guarantee_text: "If the audit doesn't identify at least 20 prioritized opportunities, we refund 100%.",
    faqs: [
      { q: "Do you cover technical SEO?", a: "Yes — crawl, indexability, sitemaps, Core Web Vitals, schema, redirects, canonicals." },
      { q: "Do you provide a call?", a: "Loom on Core tier; live 60-min Zoom on Pro." },
    ],
    reviews: DEFAULT_REVIEWS,
  },

  "blog-content-plan": {
    tagline: "Monthly content strategy: clusters, calendars & briefs.",
    hero_bullets: [
      "Keyword research mapped to intent",
      "30-topic editorial calendar per month",
      "Brief templates ready for writers",
    ],
    process: [
      { title: "Discovery", body: "We align on your audience, ICP, and the SERPs you want to own." },
      { title: "Cluster mapping", body: "We build pillar + supporting topic clusters tied to product themes." },
      { title: "Calendar + briefs", body: "30 topics, sequenced, with briefs your writers can run with." },
    ],
    examples: EXAMPLE_IMAGES,
    benefits: [
      { icon: SparkleIcon, title: "Strategy clarity", body: "Stop guessing what to publish next. We give you a calendar with rationale." },
      { icon: Layers, title: "Topical clusters", body: "Every topic ties back to a pillar to compound topical authority." },
      { icon: Target, title: "Search-intent aligned", body: "Each brief includes intent type, SERP fingerprint, and angle." },
      { icon: PenTool, title: "Writer-ready briefs", body: "Outlines, internal links, references — no more back-and-forth." },
    ],
    side_logos: ["Cluster Labs", "Brief Co", "Topical Authority"],
    guarantee_text: "Free re-scope if you don't see at least 20 high-intent, low-difficulty topics in your delivery.",
    faqs: [
      { q: "What format are briefs in?", a: "Notion, Google Docs, or Markdown — your pick." },
      { q: "Do you write the content?", a: "Not on this service — pair with our editorial bench if you'd like writing." },
    ],
    reviews: DEFAULT_REVIEWS,
  },
};
